import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { BackToTop } from "@/components/feature/BackToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, getArticleBySlug, getCategoryBySlug, siteInfo } from "@/mocks/articles";
import { usePageMeta, SITE_ORIGIN } from "@/hooks/usePageMeta";
import { formatDateCN } from "@/utils/format";
import ArticleToc from "./components/ArticleToc";
import ArticleTimeline from "./components/ArticleTimeline";

export default function ArticleDetail() {
  const { slug = "" } = useParams();
  const article = getArticleBySlug(slug);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter(
      (item) => item.categorySlug === article.categorySlug && item.slug !== article.slug
    );
    const others = articles.filter(
      (item) => item.categorySlug !== article.categorySlug && item.slug !== article.slug
    );
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  const tocItems = useMemo(() => {
    if (!article) return [];
    return [
      { id: "key-points", label: "核心结论" },
      { id: "timeline", label: "事件时间线" },
      ...article.sections.map((section, index) => ({ id: `section-${index}`, label: section.heading })),
    ];
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) {
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "内容不存在｜看料吃瓜",
        url: `${SITE_ORIGIN}/insights`,
        inLanguage: "zh-CN",
      };
    }
    const category = getCategoryBySlug(article.categorySlug);
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: article.title,
          description: article.dek,
          datePublished: article.date,
          dateModified: article.date,
          inLanguage: "zh-CN",
          author: { "@type": "Person", name: article.author },
          publisher: { "@type": "Organization", name: siteInfo.name, url: `${SITE_ORIGIN}/` },
          mainEntityOfPage: `${SITE_ORIGIN}/insights/${article.slug}`,
          articleSection: category?.name,
          keywords: article.tags.join(","),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "深度复盘", item: `${SITE_ORIGIN}/insights` },
            { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_ORIGIN}/insights/${article.slug}` },
          ],
        },
      ],
    };
  }, [article]);

  usePageMeta({
    title: article ? `${article.title}｜看料吃瓜深度复盘` : "内容不存在｜看料吃瓜",
    description: article
      ? `${article.dek} 看料吃瓜为你梳理事件时间线与背景脉络。`
      : "你访问的复盘内容不存在或已被移除，可以返回深度复盘合集继续浏览。",
    path: `/insights/${slug}`,
    keywords: article ? article.tags.join(",") : "看料吃瓜",
    type: "article",
    jsonLd,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex max-w-editorial flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <i className="ri-file-unknow-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">这篇复盘暂时找不到</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            它可能已被移动或尚未发布。你可以回到深度复盘合集，继续浏览其他内容。
          </p>
          <Link
            to="/insights"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回深度复盘
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
          <div className="mx-auto flex max-w-editorial flex-wrap items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/insights" className="transition-colors hover:text-primary-600">
              深度复盘
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="line-clamp-1 text-foreground-800">{article.title}</span>
          </div>
        </nav>

        <article>
          <header className="border-b border-background-200 bg-background-100">
            <div className="mx-auto max-w-editorial px-4 py-10 md:px-6 md:py-14">
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={`/insights?category=${article.categorySlug}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                >
                  <i className={`${category?.icon} text-sm`} aria-hidden="true"></i>
                  {category?.name}
                </Link>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                  <i className="ri-timer-line" aria-hidden="true"></i>
                  {article.readMinutes} 分钟读完
                </span>
              </div>

              <h1 className="mt-5 max-w-4xl font-heading text-3xl font-bold leading-[1.25] text-foreground-950 md:text-[42px]">
                {article.title}
              </h1>
              <p className="mt-5 max-w-3xl text-base leading-loose text-foreground-600">{article.dek}</p>

              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-background-300 pt-6 text-sm text-foreground-600">
                <span className="inline-flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 font-heading text-xs font-bold text-background-50">
                    {article.author.slice(0, 1)}
                  </span>
                  <span className="text-foreground-900">{article.author}</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  <time dateTime={article.date}>{formatDateCN(article.date)}</time>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                  {article.tags.join(" · ")}
                </span>
              </div>
            </div>
          </header>

          <div className="mx-auto max-w-editorial px-4 md:px-6">
            <div className="mt-8 h-[240px] overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="mx-auto max-w-editorial px-4 py-12 md:px-6 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-8">
                <section id="key-points" className="scroll-mt-28 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-focus-3-line text-primary-500" aria-hidden="true"></i>
                    核心结论
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {article.keyPoints.map((point) => (
                      <li key={point} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" aria-hidden="true"></span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </section>

                <section id="timeline" className="mt-10 scroll-mt-28">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    <i className="ri-history-line text-primary-500" aria-hidden="true"></i>
                    事件时间线
                  </h2>
                  <ArticleTimeline items={article.timeline} />
                </section>

                {article.sections.map((section, index) => (
                  <section key={section.heading} id={`section-${index}`} className="mt-10 scroll-mt-28">
                    <h2 className="font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph} className="text-[15px] leading-[2] text-foreground-700">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}

                {article.quote && (
                  <blockquote className="mt-10 border-l-2 border-accent-500 bg-background-100 px-6 py-5">
                    <i className="ri-double-quotes-l text-xl text-accent-500" aria-hidden="true"></i>
                    <p className="mt-2 font-heading text-lg leading-relaxed text-foreground-900">{article.quote}</p>
                  </blockquote>
                )}

                <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
                  <div className="flex flex-wrap items-center gap-2">
                    {article.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                        # {tag}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/insights"
                    className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
                  >
                    <i className="ri-arrow-left-line" aria-hidden="true"></i>
                    返回复盘合集
                  </Link>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28">
                  <ArticleToc items={tocItems} />

                  <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
                    <p className="text-xs font-medium tracking-widest text-foreground-500">本文作者</p>
                    <div className="mt-3 flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                        {article.author.slice(0, 1)}
                      </span>
                      <div>
                        <p className="font-heading text-sm font-semibold text-foreground-950">{article.author}</p>
                        <p className="text-xs text-foreground-500">看料吃瓜 内容团队</p>
                      </div>
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                      长期关注内容行业与网络舆情，习惯先查证再下笔。
                    </p>
                  </div>

                  <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 p-5">
                    <p className="font-heading text-sm font-semibold text-primary-800">发现文中事实有误？</p>
                    <p className="mt-2 text-xs leading-relaxed text-primary-700">
                      欢迎把依据发给我们，核实后会在文末公开更正说明。
                    </p>
                    <a
                      href={`mailto:${siteInfo.email}`}
                      className="mt-3 inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-primary-700 transition-colors hover:text-primary-600"
                    >
                      <i className="ri-mail-line" aria-hidden="true"></i>
                      {siteInfo.email}
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-editorial px-4 py-14 md:px-6 md:py-16">
            <div className="flex items-end justify-between border-b border-background-200 pb-5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">继续阅读</h2>
              <Link to="/insights" className="text-sm text-foreground-600 transition-colors hover:text-primary-600">
                查看全部
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.slug} article={item} variant="standard" />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}